import React, { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import BadgeStatus from "Components/Generic/BadgeStatus";
import { useAppState } from "Context/StateContext";
import { format, utcToZonedTime } from "date-fns-tz";
import { Member } from "Models/members/member";
import { MemberAvailability } from "Models/members/member.availability";
import { Filters, Searches } from "Models/shipeezi.table";
import { useTranslation } from "react-i18next";
import AvailabilityService from "Services/availability.service";
import { MemberAvailabilityType } from "Util/enums/members";
import { getStatusBadgeColorsByAvailability } from "Util/fleetStatus";
import { getHoursDiffering } from "Util/formatDateAndTime";
import { handlesGrid } from "Util/handlesGrid";
import { AvailabilityKeys } from "Util/queries";

import { ColumnDateShow, ColumnStatus } from "./styles";

interface IAvailabilitySection {
  member: Member;
}

export const AvailabilitySection = (props: IAvailabilitySection) => {
  const { member } = props;
  const { t: translate } = useTranslation();
  const { currentUser, userTimezone } = useAppState();
  const { id: memberId } = member;

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const { handleCurrentPage, handlePageSize } = handlesGrid({
    setPage,
    setPageSize,
  });

  const { data: availabilities = [], isFetching } = useQuery({
    queryKey: AvailabilityKeys.getAll([page, pageSize, filters, searches]),
    queryFn: async () => {
      const response = await AvailabilityService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        searchByOr: searches?.length ? JSON.stringify(searches) : "",
        include: JSON.stringify([
          {
            association: "members",
            where: {
              id: memberId,
            },
            attributes: ["id", "firstName", "lastName", "fullName"],
          },
        ]),
        ...filters,
      });

      if (response?.data && Array.isArray(response.data)) return response.data;

      return [];
    },
    enabled: !!memberId,
    refetchOnWindowFocus: false,
  });

  const { data: availabilitiesTotal } = useQuery({
    queryKey: AvailabilityKeys.count([page, pageSize, filters]),
    queryFn: async () => {
      const response = await AvailabilityService.count({
        companyId: currentUser?.companyId,
        include: JSON.stringify([
          {
            association: "members",
            where: {
              id: memberId,
            },
          },
        ]),
        distinctCount: true,
        ...filters,
      });

      if (response?.data) return response.data;

      return 0;
    },
  });

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    const filtersKeys = Object.getOwnPropertyNames(value);

    setFilters(filtersKeys && filtersKeys?.length ? value : undefined);
  };

  const columns: Column[] = [
    {
      name: "type",
      title: translate("type"),
    },
    {
      name: "dateFrom",
      title: translate("from"),
      hideSearch: true,
      formatComponent: (_, row?: MemberAvailability) => {
        const startDate = row?.dateFrom;

        if (startDate) {
          if (row?.allDayAbsence) {
            return (
              <ColumnDateShow>
                <span>
                  {format(
                    utcToZonedTime(
                      startDate,
                      userTimezone.name || Intl.DateTimeFormat().resolvedOptions().timeZone
                    ),
                    "dd/MM/yyyy"
                  )}
                </span>
              </ColumnDateShow>
            );
          }

          return (
            <ColumnDateShow>
              <span>
                {format(
                  utcToZonedTime(
                    startDate,
                    userTimezone.name || Intl.DateTimeFormat().resolvedOptions().timeZone
                  ),
                  "dd/MM/yyyy"
                )}
              </span>
              <span>
                {format(
                  utcToZonedTime(
                    startDate,
                    userTimezone.name || Intl.DateTimeFormat().resolvedOptions().timeZone
                  ),
                  "hh:mm a"
                )}
              </span>
            </ColumnDateShow>
          );
        }

        return "-";
      },
    },
    {
      name: "dateTo",
      title: translate("to"),
      hideSearch: true,
      formatComponent: (_, row?: MemberAvailability) => {
        const startDate = row?.dateFrom;
        const endDate = row?.dateTo;

        if (row?.allDayAbsence && endDate) {
          return (
            <ColumnDateShow>
              <span>
                {format(
                  utcToZonedTime(
                    endDate,
                    userTimezone.name || Intl.DateTimeFormat().resolvedOptions().timeZone
                  ),
                  "dd/MM/yyyy"
                )}
              </span>
            </ColumnDateShow>
          );
        }

        if (!row?.allDayAbsence && startDate) {
          return (
            <ColumnDateShow>
              <span>
                {format(
                  utcToZonedTime(
                    startDate,
                    userTimezone.name || Intl.DateTimeFormat().resolvedOptions().timeZone
                  ),
                  "dd/MM/yyyy"
                )}
              </span>
              {endDate && (
                <span>
                  {format(
                    utcToZonedTime(
                      endDate,
                      userTimezone.name || Intl.DateTimeFormat().resolvedOptions().timeZone
                    ),
                    "hh:mm a"
                  )}
                </span>
              )}
            </ColumnDateShow>
          );
        }

        return "-";
      },
    },
    {
      name: "hours",
      title: translate("hours"),
      hideSearch: true,
      formatComponent: (_, row?: MemberAvailability) => {
        if (row?.allDayAbsence) {
          return "-";
        }

        const startDate = row?.dateFrom ? new Date(row.dateFrom) : null;
        const endDate = row?.dateTo ? new Date(row.dateTo) : null;

        if (startDate && endDate) {
          startDate.setHours(startDate.getHours());
          startDate.setMinutes(startDate.getMinutes());

          endDate.setHours(endDate.getHours());
          endDate.setMinutes(endDate.getMinutes());

          return <span>{getHoursDiffering(startDate, endDate)}</span>;
        }

        return "-";
      },
    },
    {
      name: "members",
      hideSearch: true,
      title: translate("createdBy"),
      formatText: (member: MemberAvailability["members"]) => member?.[0]?.fullName ?? "-",
    },
    {
      name: "status",
      hideSearch: true,
      title: translate("status"),
      formatComponent: (_, row?: MemberAvailability) => {
        if (!row) return "-";

        const { label, background, color } = getStatusBadgeColorsByAvailability(row, translate);

        return (
          <ColumnStatus>
            <BadgeStatus
              value={label}
              useExternalColor
              backgroundColor={background}
              textColor={color}
            />
          </ColumnStatus>
        );
      },
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          column: "type",
          name: translate("type"),
          options: Object.values(MemberAvailabilityType).map((option) => ({
            label: option,
            value: option,
          })),
        },
      ],
      onChange: handleFilter,
    },
  };

  return (
    <>
      <ShipeeziNewGrid
        data={availabilities || []}
        totalItems={Number(availabilitiesTotal) || 0}
        columns={columns}
        showSelection={false}
        showSort={false}
        currentPage={page}
        pageSize={pageSize}
        pageSizeList={[10, 20, 40, 60]}
        onChangePageSize={handlePageSize}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        showFooterGrid
        positionActionsColumn="right"
        keyExtractor={(data) => data?.id?.toString()}
        loadingData={isFetching}
        emptyData={translate("noInformationToBeShown")}
        generalActions={generalActions}
      />
    </>
  );
};
